import React, { FC, ReactElement, useState } from 'react';
import { connect } from 'react-redux';
import { Dropdown, Menu, message } from 'antd';
import { EditOutlined, PushpinFilled } from '@ant-design/icons';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import OWButton from '../ow_button/OWButton';
import { isAdminUser, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { exportTemplatesRestCrudThunks } from '../../thunks/export_templates_thunks';
import { ExportSorting, fetchExportFields, queueExport } from '../../thunks/export_thunks';
import {
	canEditTemplate,
	DEFAULT_FILE_FORMAT,
	defaultColumns,
	ExportColumn,
	ExportField,
	ExportFilters,
	ExportTemplate,
	ExportTemplatesState,
	ExportViewer,
	fileFormatSuffix,
	showExportQueued,
} from './ExportColumnsUtils';
import ExportModal, { pinIconClassName } from './ExportModal';

const PINNED_TEMPLATES_TARGET_COLLECTION = 'exportTemplatesPinned';
const PINNED_TEMPLATES_PAGE_SIZE = 50;
const PINNED_TEMPLATES_SORTING: ExportSorting = { sort_by: 'name', order: 'ascend' };
const STANDARD_EXPORT_KEY = 'standard';
const CUSTOMIZE_KEY = 'customize';
const LOAD_MORE_PINNED_KEY = 'loadMorePinned';

interface ExportMenuProps {
	entityType: string;
	title: string;
	userType: string;
	filters: ExportFilters;
	sorting: ExportSorting;
	children: ReactElement;
	currentUser: ExportViewer;
	exportTemplates: ExportTemplatesState;
	fetchPinnedTemplates: (
		pagination: { current: number; pageSize: number },
		addToTargetCollection: boolean
	) => Promise<unknown>;
	fetchFields: (filters: ExportFilters) => Promise<{ fields: ExportField[]; count: number }>;
	exportColumns: (
		filters: ExportFilters,
		sorting: ExportSorting,
		columns: ExportColumn[],
		fileFormat: string
	) => Promise<string>;
	updateTemplate: (template: ExportTemplate) => Promise<ExportTemplate>;
}

const ExportMenu: FC<ExportMenuProps> = ({
	entityType,
	title,
	userType,
	filters,
	sorting,
	children,
	currentUser,
	exportTemplates,
	fetchPinnedTemplates,
	fetchFields,
	exportColumns,
	updateTemplate,
}) => {
	const [menuVisible, setMenuVisible] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [unpinningTemplateId, setUnpinningTemplateId] = useState<number>(null);
	const isAdmin = isAdminUser(currentUser);
	const pinnedTemplates: ExportTemplate[] = getRecordsForTargetCollection(
		exportTemplates,
		PINNED_TEMPLATES_TARGET_COLLECTION
	);
	const pinnedTemplatesCount = nullSafeGetOrElse(
		`${PINNED_TEMPLATES_TARGET_COLLECTION}.count`,
		exportTemplates,
		0
	);

	const showTemplatesLoadError = (error: string) =>
		message.error(error || 'Could not load the templates');

	const loadPinnedTemplates = () =>
		fetchPinnedTemplates({ current: 1, pageSize: PINNED_TEMPLATES_PAGE_SIZE }, false).catch(
			showTemplatesLoadError
		);

	const loadMorePinnedTemplates = () => {
		const nextPage = Math.floor(pinnedTemplates.length / PINNED_TEMPLATES_PAGE_SIZE) + 1;
		fetchPinnedTemplates({ current: nextPage, pageSize: PINNED_TEMPLATES_PAGE_SIZE }, true).catch(
			showTemplatesLoadError
		);
	};

	const handleMenuVisibleChange = (visible: boolean) => {
		setMenuVisible(visible);
		if (visible) {
			loadPinnedTemplates();
		}
	};

	const startExport = (columnsPromise: Promise<ExportColumn[]>, fileFormat: string) => {
		setExporting(true);
		columnsPromise
			.then((columns) => exportColumns(filters, sorting, columns, fileFormat))
			.then(showExportQueued)
			.catch((error) => message.error(error || 'Could not start the export'))
			.finally(() => setExporting(false));
	};

	const handleMenuClick = ({ key }: { key: string }) => {
		if (key === LOAD_MORE_PINNED_KEY) {
			// The dropdown has already asked to close on this click.
			setMenuVisible(true);
			loadMorePinnedTemplates();
			return;
		}
		setMenuVisible(false);
		if (key === STANDARD_EXPORT_KEY) {
			startExport(
				fetchFields(filters).then((catalog) => defaultColumns(catalog.fields)),
				DEFAULT_FILE_FORMAT
			);
		} else if (key === CUSTOMIZE_KEY) {
			setModalVisible(true);
		} else {
			const template = pinnedTemplates.find((candidate) => `${candidate.id}` === key);
			startExport(Promise.resolve(template.columns), template.fileFormat);
		}
	};

	const unpinTemplate = (template: ExportTemplate) => () => {
		setUnpinningTemplateId(template.id);
		updateTemplate({ ...template, isPinned: false })
			.then(loadPinnedTemplates)
			.catch((error) => message.error(error || 'Could not update the template'))
			.finally(() => setUnpinningTemplateId(null));
	};

	const closeModal = () => setModalVisible(false);

	const templateItems =
		pinnedTemplates.length > 0
			? [
					<Menu.Divider key="templatesDivider" />,
					<Menu.ItemGroup key="templates" title="Templates">
						{pinnedTemplates.map((template) => {
							const canUnpin = canEditTemplate(template, currentUser.email, isAdmin);
							return (
								<Menu.Item key={template.id}>
									<div className="flex items-center justify-between gap-6">
										<span>
											{template.name}{' '}
											<span className="text-character-secondary">
												{fileFormatSuffix(template.fileFormat)}
											</span>
										</span>
										<span onClick={stopDefaultEvents}>
											<OWButton
												disabled={!canUnpin}
												loading={unpinningTemplateId === template.id}
												icon={
													<PushpinFilled
														className={canUnpin ? pinIconClassName(userType) : undefined}
													/>
												}
												onClick={unpinTemplate(template)}
											/>
										</span>
									</div>
								</Menu.Item>
							);
						})}
						{pinnedTemplates.length < pinnedTemplatesCount ? (
							<Menu.Item key={LOAD_MORE_PINNED_KEY}>
								<span className="text-purple-6">Load more</span>
							</Menu.Item>
						) : null}
					</Menu.ItemGroup>,
			  ]
			: null;

	const menu = (
		<Menu onClick={handleMenuClick}>
			<Menu.Item key={STANDARD_EXPORT_KEY}>
				Standard Export <span className="text-character-secondary">(.csv)</span>
			</Menu.Item>
			{templateItems}
			<Menu.Divider />
			<Menu.Item key={CUSTOMIZE_KEY} icon={<EditOutlined />}>
				Customize
			</Menu.Item>
		</Menu>
	);

	return (
		<>
			<Dropdown
				overlay={menu}
				trigger={['click']}
				placement="bottomRight"
				visible={menuVisible}
				onVisibleChange={handleMenuVisibleChange}
				disabled={exporting}
			>
				{children}
			</Dropdown>
			{modalVisible ? (
				<ExportModal
					entityType={entityType}
					title={title}
					userType={userType}
					filters={filters}
					sorting={sorting}
					onClose={closeModal}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	exportTemplates: state.export_templates,
});

const mapDispatchToProps = (
	dispatch,
	ownProps: Pick<ExportMenuProps, 'userType' | 'entityType'>
): Pick<
	ExportMenuProps,
	'fetchPinnedTemplates' | 'fetchFields' | 'exportColumns' | 'updateTemplate'
> => {
	const templateThunks = exportTemplatesRestCrudThunks(ownProps.userType);
	return {
		fetchPinnedTemplates: (pagination, addToTargetCollection) =>
			dispatch(
				templateThunks.read(
					{ entityType: ownProps.entityType, isPinned: true },
					PINNED_TEMPLATES_TARGET_COLLECTION,
					pagination,
					PINNED_TEMPLATES_SORTING,
					{},
					addToTargetCollection
				)
			),
		fetchFields: (filters) =>
			dispatch(fetchExportFields(ownProps.userType, ownProps.entityType)(filters)),
		exportColumns: (filters, sorting, columns, fileFormat) =>
			dispatch(
				queueExport(ownProps.userType, ownProps.entityType)(filters, sorting, columns, fileFormat)
			),
		updateTemplate: (template) => dispatch(templateThunks.update(template)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ExportMenu);
